"use client";

import { getStoredConsent, queueConsentModeUpdate } from "@/lib/analytics/consent";
import { UK_GA_MEASUREMENT_ID } from "@/lib/analytics/trackingConsent";
import { sanitizeAnalyticsProperties } from "@/lib/privacy/urlSanitization";

type DataLayerEventProperties = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: {
      (command: string, ...args: unknown[]): void;
      q?: unknown[][];
    };
  }
}

function getConsent(category: "analytics" | "marketing") {
  if (typeof window === "undefined") return null;
  const stored = getStoredConsent();
  return stored?.[category] === "granted" ? stored : null;
}

export function pushAnalyticsEvent(name: string, properties?: DataLayerEventProperties) {
  const consent = getConsent("analytics");
  if (!consent || !queueConsentModeUpdate(consent)) return;
  window.dataLayer = window.dataLayer || [];
  const safeProperties = sanitizeAnalyticsProperties(properties);
  window.dataLayer.push({
    event: name,
    ...safeProperties,
  });

  // The current GTM container does not forward these new custom events.
  // Send only lesson-video events to the existing GA4 destination after consent.
  if (name.startsWith("lesson_video_")) {
    window.gtag?.("event", name, { ...safeProperties, send_to: UK_GA_MEASUREMENT_ID });
  }

  // Clarity custom events are intentionally name-only. Properties remain in
  // GTM/GA4 so we do not send customer or booking details to session replay.
  if (typeof window.clarity === "function") {
    window.clarity("event", name);
  }
}

export function pushMarketingEvent(name: string, properties?: DataLayerEventProperties) {
  const consent = getConsent("marketing");
  if (!consent || !queueConsentModeUpdate(consent)) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: name,
    ...sanitizeAnalyticsProperties(properties),
  });
}
